<template>
    <div>
        <loading-container v-if="loading"></loading-container>

        <div v-else class="container">
            <div v-if="!person.data.address" class="alert alert-warning">
                <strong>Warning!</strong> The subscriber has no address.
            </div>

            <form @submit.prevent="submit" class="form-horizontal">
                <div class="row">
                    <div class="col-xs-6">
                        <help-text>
                            <h3>
                                Create Subscription
                            </h3>

                            <p>
                                Enter the portal that the subscription subscribes to, and select a subscription type.
                            </p>

                            <p>
                                You may also attach a subscription period to the subscription. The subscription will be
                                active during the period selected here.
                            </p>

                            <p>
                                You may create more that one period in order to split the invoice into multiple parts.
                            </p>

                            <p>
                                If no periods are created, the subscription will count as a trial subscription. The
                                subscription will also have trial status until the start of the first subscription
                                period, if one is created.
                            </p>

                            <button
                                    type="button"
                                    class="btn btn-sm btn-black"
                                    @click="addSubscriptionPeriod"
                            >
                                <i class="fa fa-plus"></i> Add Subscription Period
                            </button>
                        </help-text>
                    </div>

                    <div class="col-xs-18">
                        <basic-information
                                v-model="subscription"
                                :portals="portals"
                                :subscription-types="subscriptionTypes"
                                :errors="errors"
                        ></basic-information>

                        <subscription-period
                                v-for="(subscriptionPeriod, index) in subscription.subscription_periods"
                                :key="index"
                                :subscription-period="subscriptionPeriod"
                                :subscription="subscription"
                                :index="index"
                                :errors="errors"
                                @input="subscription.subscription_periods.splice(index, 1, $event)"
                                @remove="subscription.subscription_periods.splice(index, 1)"
                        ></subscription-period>

                        <change-defaults
                                v-model="subscription"
                                :errors="errors"
                        ></change-defaults>

                        <advanced-setup
                                v-model="subscription"
                                :other-payer="person.data.company"
                                :errors="errors"
                        ></advanced-setup>
                    </div>
                </div>

                <hr class="panel-separator">

                <div class="row">
                    <div class="col-xs-offset-6 col-xs-18">
                        <button type="submit" class="btn btn-black" style="margin-bottom: 22px">
                            <i class="fa fa-save"></i> Save
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
    import http from '@/http'
    import moment from 'moment'
    import notify from '@/notify'
    import store from '@/store'
    import AdvancedSetup from '@/pages/People/People/Edit/Tabs/CreateSubscription/Panels/AdvancedSetup'
    import BasicInformation from '@/pages/People/People/Edit/Tabs/CreateSubscription/Panels/BasicInformation'
    import ChangeDefaults from '@/pages/People/People/Edit/Tabs/CreateSubscription/Panels/ChangeDefaults'
    import Errors from '@/mixins/Errors'
    import HelpText from '@/components/HelpText'
    import Load from '@/mixins/Load'
    import LoadingContainer from '@/components/Layout/LoadingContainer'
    import SubscriptionPeriod from '@/pages/People/People/Edit/Tabs/CreateSubscription/Panels/SubscriptionPeriod'

    export default {
        components: {
            AdvancedSetup,
            BasicInformation,
            ChangeDefaults,
            HelpText,
            LoadingContainer,
            SubscriptionPeriod
        },

        mixins: [Errors, Load],

        model: {
            prop: 'person',
            event: 'person-updated'
        },

        props: ['person'],

        data() {
            return {
                subscription: {
                    has_automatic_renewal: true,
                    is_free: false,
                    user_id: store.user.id,
                    renewal_responsible_id: store.user.id,
                    subscription_periods: [],
                    other_payer_id: this.person.data.company_id
                },
                portals: null,
                subscriptionTypes: null
            }
        },

        mounted() {
            this.load({
                portals: '/api/v2/portals',
                subscriptionTypes: '/api/v2/subscription-types',
            })
        },

        watch: {
            'subscription.portal_id'() {
                this.subscription.subscription_type_id = null
            },

            'subscription.subscription_type_id'(value) {
                if (!value) {
                    return
                }

                let subscriptionType = this.subscriptionTypes.data.find(subscriptionType => {
                    return subscriptionType.id === parseInt(value)
                })

                this.subscription.price = subscriptionType.price

                this.subscription.period = subscriptionType.period

                this.subscription.closes_after = subscriptionType.closes_after

                this.subscription.seats = subscriptionType.seats
            },

            'subscription.price'(value) {
                this.subscription.subscription_periods = this.subscription.subscription_periods.map(period => {
                    return {...period, price: value}
                })
            },

            'subscription.period'(value) {
                this.subscription.subscription_periods = this.subscription.subscription_periods.map(period => {
                    return {...period, ends_at: moment(period.starts_at).add(value, 'months').format('YYYY-MM-DD')}
                })
            },

            'subscription.closes_after'(value) {
                this.subscription.subscription_periods = this.subscription.subscription_periods.map(period => {
                    return {...period, closes_at: moment(period.starts_at).add(value, 'days').format('YYYY-MM-DD')}
                })
            }
        },

        methods: {
            submit() {
                http.post('/api/v1/subscriptions', {
                    ...this.subscription,
                    subscriber: {id: this.person.data.id, type: this.person.data.type}
                })
                    .then(() => {
                        notify.success('The subscription was saved')

                        this.$router.push('.')
                    })
                    .catch(error => {
                        if (error.response.status === 400) {
                            return notify.error(error.response.data.message)
                        }

                        throw error
                    })
                    .catch(this.catchValidationErrors)
            },

            addSubscriptionPeriod() {
                this.subscription.subscription_periods.push({
                    price: this.subscription.price,
                    starts_at: moment().format('YYYY-MM-DD'),
                    ends_at: moment().add(this.subscription.period, 'months').format('YYYY-MM-DD'),
                    closes_at: moment().add(this.subscription.closes_after, 'days').format('YYYY-MM-DD')
                })
            }
        }
    }
</script>
